import {
  Alert,
  Box,
  Button,
  Grid,
  Group,
  Space,
  Spoiler,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'tabler-icons-react';
import { BusinessTypes } from '../../enums/business-type';
import { exposePageWithError } from '../../helpers/merchant-onboarding';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import { UpdateMerchantOnboardingFormValues } from '../../type/update-merchant-onboarding-form-values.types';
import UpdateBusinessInformation from '../admin-update-merchant/update-business-information.component';
import UpdateGeneralInfo from '../admin-update-merchant/update-general-info.component';
import UpdateIncorporatorsSignatories from '../admin-update-merchant/update-incorporators-signatories.component';
import { MdrFee } from '../../type/onboard-merchant-request.types';
import { MdrInputField } from '../filters/alternative-merchant-management-filter.component';
import { CurrenciesData } from '../../data/currencies-data.array';
import { Roles } from '../../enums/roles-enum';

interface UpdateOnboardingProps {
  handleSubmit: (values: UpdateMerchantOnboardingFormValues) => Promise<void>;
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>;
  initialBusinessHandle: string;
  isNonAdminInterface: boolean;
  declinedReason?: string;
  loading: boolean;
  disabledReason?: string;
  isActive?: boolean;
  mdrFees?: MdrFee[];
  setMdrFees: React.Dispatch<React.SetStateAction<MdrFee[] | undefined>>;
  parentTypeId?: number;
}
function UpdateOnboarding(props: Readonly<UpdateOnboardingProps>) {
  const {
    handleSubmit,
    form,
    initialBusinessHandle,
    isNonAdminInterface,
    declinedReason,
    loading,
    disabledReason,
    isActive,
    mdrFees,
    setMdrFees,
    parentTypeId,
  } = props;
  const [generalHasError, setGeneralHasError] = useState(false);
  const [incorporatorSignatoriesHasError, setIncorporatorSignatoriesHasError] =
    useState(false);
  const [businessInfoHasErrors, setBusinessInfoHasErrors] = useState(false);
  const [businessHandleError, setBusinessHandleError] = useState('');
  const submitRef = useRef();
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    exposePageWithError(
      form,
      generalHasError,
      setGeneralHasError,
      incorporatorSignatoriesHasError,
      setIncorporatorSignatoriesHasError,
      businessInfoHasErrors,
      setBusinessInfoHasErrors
    );
  }, [form.errors]);

  const validateForm = async () => {
    let hasBusinessErr = false;

    //No need to validate for starter since starter has autogenerated business handle
    if (
      initialBusinessHandle === form.values.businessHandle ||
      form.values.businessTypeId === BusinessTypes.STARTER
    ) {
      // @ts-ignore
      submitRef?.current?.click();
      return;
    }

    const BUSINESS_HANDLE_VALIDATION_URL = '/web/onboard/businesshandles';
    if (form.values.businessHandle?.trim().length > 0) {
      try {
        await axiosPrivate.post(BUSINESS_HANDLE_VALIDATION_URL, {
          businessHandle: form.values.businessHandle,
        });
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        //We do this because validation for businesshandle in schema is any()
        hasBusinessErr = true;
        if (!/^([A-Za-z]|[0-9]|_|-)+$/.test(form.values.businessHandle)) {
          form.setFieldError(
            'businessHandle',
            'Business handle can only include alphanumeric, underscores, and dashes'
          );
          setBusinessHandleError(
            'Business handle can only include alphanumeric, underscores, and dashes'
          );
        } else if (error.error) {
          form.setFieldError('businessHandle', error.error);
          setBusinessHandleError(error.error);
        } else {
          form.setFieldError('businessHandle', 'An Unexpected Error Occurred');
          setBusinessHandleError('An Unexpected Error Occurred');
        }
      }
    } else if (form.values.businessHandle?.trim().length < 1) {
      form.setFieldValue('businessHandle', '');
      setBusinessHandleError('This field is invalid');
    }
    // @ts-ignore
    if (!hasBusinessErr) submitRef?.current?.click();
  };

  const getIncorporatorSignatoriesTabLabel = () => {
    if (form.values.businessTypeId === BusinessTypes.STARTER)
      return 'Signatories';
    if (form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR)
      return 'Proprietor & Signatories';
    return 'Incorporators & Signatories';
  };

  const getRoleIndex = (role: Roles): number => {
    const rolesArray = Object.values(Roles);
    return rolesArray.indexOf(role);
  };

  const isSaveUnderBranch =
    parentTypeId === getRoleIndex(Roles.MERCHANT_BRANCH);

  return (
    <>
      {disabledReason && !isActive && (
        <Alert title="Disabled Reason" color="red" mb={22}>
          <Spoiler
            maxHeight={110}
            showLabel={<Text size="sm">Show more</Text>}
            hideLabel={<Text size="sm">Hide</Text>}
          >
            {disabledReason}
          </Spoiler>
        </Alert>
      )}
      {declinedReason && (
        <Alert
          title="Declined Reason"
          color="red"
          mb={22}
          data-cy="declinedReason"
        >
          <Spoiler
            maxHeight={110}
            showLabel={<Text size="sm">Show more</Text>}
            hideLabel={<Text size="sm">Hide</Text>}
          >
            {declinedReason}
          </Spoiler>
        </Alert>
      )}
      {generalHasError && (
        <Text color="red">Invalid field/s in General Information Tab</Text>
      )}
      {incorporatorSignatoriesHasError && (
        <Text color="red">
          Invalid field/s in Incorporators & Signatories Tab
        </Text>
      )}
      {businessInfoHasErrors && (
        <Text color="red">Invalid field/s in Business Information Tab</Text>
      )}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Tabs>
          <Tabs.Tab data-cy="generalInfoTab" label="General Information">
            <UpdateGeneralInfo
              form={form}
              setBusinessHandleError={setBusinessHandleError}
              businessHandleError={businessHandleError}
              isNonAdminInterface={isNonAdminInterface}
            />
          </Tabs.Tab>
          {!isNonAdminInterface && (
            <Tabs.Tab
              data-cy="incorporatorSignatoriesTab"
              label={getIncorporatorSignatoriesTabLabel()}
            >
              <UpdateIncorporatorsSignatories form={form} />
            </Tabs.Tab>
          )}

          <Tabs.Tab data-cy="businessInfoTab" label="Business Information">
            <UpdateBusinessInformation
              form={form}
              isNonAdminInterface={isNonAdminInterface}
            />
          </Tabs.Tab>
          {isSaveUnderBranch && (
            <Tabs.Tab data-cy="mdrInfoTab" label="MDR">
              <Title order={3}>Merchant Discount Rate</Title>
              <Grid>
                {CurrenciesData.map((currency) => {
                  return (
                    <MdrInputField
                      key={currency.value}
                      currency={currency}
                      mdrFees={mdrFees}
                      mode={undefined}
                      setMdrFees={setMdrFees}
                    />
                  );
                })}
              </Grid>
            </Tabs.Tab>
          )}
        </Tabs>
        <Group
          style={{ width: '100%' }}
          grow
          position="apart"
          align="flex-start"
          mt={30}
        >
          {Object.keys(form.errors).length > 0 ? (
            <Alert
              icon={<AlertCircle size={16} />}
              title="Invalid fields"
              color="red"
              variant="outline"
            >
              Please check fields for errors
            </Alert>
          ) : (
            <Space />
          )}
          <Box my="md" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              // @ts-ignore
              ref={submitRef}
              style={{ display: 'none' }}
            ></button>
            <Button
              loading={loading}
              onClick={validateForm}
              data-cy="submitButton"
            >
              Submit
            </Button>
          </Box>
        </Group>
      </form>
    </>
  );
}

export default UpdateOnboarding;
